/**
 * Security — collect, analyze and prioritize code/dependency risk and lead
 * it into controlled remediation.
 *
 * Availability is BETA on purpose: what ships today is the operational
 * posture pipeline (gitleaks, npm-audit, semgrep, osv, trivy adapters +
 * codebase assessments/signals, mig 132/136) and governed remediation via
 * the normal mission flow. Scheduled scans, first-class findings management
 * and security reporting do NOT exist — the manifest claims analysis of
 * what the scanners actually produce, nothing more.
 */
import type { UseCaseDefinition } from "../types";

export const securityUseCase: UseCaseDefinition = {
  id: "security",
  version: 1,
  labelKey: "useCases.security.label",
  shortDescriptionKey: "useCases.security.short",
  fullDescriptionKey: "useCases.security.full",
  primaryOutcomeKey: "useCases.security.outcome",
  availability: "beta",
  applicableWorkProfiles: ["engineering", "release_devops", "manager_reviewer", "admin", "custom"],
  requiredCapabilities: ["code.read", "security.scan"],
  recommendedCapabilities: [
    "sandbox.analyze",
    "delivery.pr",
    "approvals.route",
    "notifications.post",
  ],
  suggestedBindingPurposes: ["code", "notifications", "approvals"],
  defaultExecutionMode: "read_only",
  defaultRemediationStartMode: "manual",
  // Both onboarding steps ("view scanner status", "run a read-only scan")
  // pointed at the codebase-posture page, which is staff-only while we decide
  // whether that surface is worth building properly. No customer-facing
  // replacement exists yet, so the list is empty rather than pointing people
  // at a page that answers a different question.
  onboardingStepIds: [],
  dashboardSectionIds: [
    "security.posture",
    "security.scans",
    "security.remediation",
    "security.exceptions",
  ],
  readinessCheckIds: [
    "security.codebase",
    "security.scanner-capability",
    "security.scan-status",
    "security.remediation-route",
  ],
  defaultGovernanceProfileId: "high_security",
  prerequisiteDescriptionKeys: [
    "useCases.security.prereq.codebase",
    "useCases.security.prereq.scanner",
  ],
};
